import { CloseIcon } from '../../icons'
import React, { ReactNode } from 'react'
import { Tooltip } from '../Tooltip'

export enum TagSizes {
    SMALL = 'small',
    LARGE = 'large',
}

const tagSizeClasses = {
    [TagSizes.SMALL]: 'pl-2 py-0.5 text-sm last:pr-2',
    [TagSizes.LARGE]: 'pl-2 py-0.5 text-sm last:pr-2 density-lg:pl-3 density-lg:py-1 density-lg:last:pr-3',
}

// ENUM is also used in back-end. Communicate changes in this ENUM with Back-end!!!
export enum TagColors {
    DEFAULT,
    SUCCESS,
    WARNING,
    ERROR,
    DECO_ONE,
    DECO_TWO,
    SHADE,
}

export const colorTextClasses = {
    [TagColors.DEFAULT]: 'text-accent-700',
    [TagColors.SUCCESS]: 'text-success-700',
    [TagColors.WARNING]: 'text-warning-700',
    [TagColors.ERROR]: 'text-error-700',
    [TagColors.DECO_ONE]: 'text-deco-one-700',
    [TagColors.DECO_TWO]: 'text-deco-two-700',
    [TagColors.SHADE]: 'text-shade-700',
}

export const colorBackgroundClasses = {
    [TagColors.DEFAULT]: 'bg-accent-100',
    [TagColors.SUCCESS]: 'bg-success-100',
    [TagColors.WARNING]: 'bg-warning-100',
    [TagColors.ERROR]: 'bg-error-100',
    [TagColors.DECO_ONE]: 'bg-deco-one-100 text',
    [TagColors.DECO_TWO]: 'bg-deco-two-100 text',
    [TagColors.SHADE]: 'bg-shade-100',
}

export const colorClasses = {
    [TagColors.DEFAULT]: `${colorTextClasses[TagColors.DEFAULT]} ${colorBackgroundClasses[TagColors.DEFAULT]}`,
    [TagColors.SUCCESS]: `${colorTextClasses[TagColors.SUCCESS]} ${colorBackgroundClasses[TagColors.SUCCESS]}`,
    [TagColors.WARNING]: `${colorTextClasses[TagColors.WARNING]} ${colorBackgroundClasses[TagColors.WARNING]}`,
    [TagColors.ERROR]: `${colorTextClasses[TagColors.ERROR]} ${colorBackgroundClasses[TagColors.ERROR]}`,
    [TagColors.DECO_ONE]: `${colorTextClasses[TagColors.DECO_ONE]} ${colorBackgroundClasses[TagColors.DECO_ONE]}`,
    [TagColors.DECO_TWO]: `${colorTextClasses[TagColors.DECO_TWO]} ${colorBackgroundClasses[TagColors.DECO_TWO]}`,
    [TagColors.SHADE]: `${colorTextClasses[TagColors.SHADE]} ${colorBackgroundClasses[TagColors.SHADE]}`,
}

export interface TagProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
    size?: TagSizes
    color?: TagColors | null
    removable?: boolean
    removeClickCallback?: (event: React.MouseEvent<HTMLDivElement>) => void
    removeMouseDownCallback?: (event: React.MouseEvent<HTMLDivElement>) => void
    removeTouchEndCallback?: (event: React.TouchEvent<HTMLDivElement>) => void
    isInteractive?: boolean
    children: ReactNode
}

export const Tag = ({
    children,
    size = TagSizes.LARGE,
    color = TagColors.DEFAULT,
    removable = false,
    removeClickCallback,
    removeMouseDownCallback,
    removeTouchEndCallback,
    isInteractive = false,
    ...props
}: TagProps) => {
    const colorToUse = color ?? TagColors.DEFAULT

    return (
        <Tooltip content={children} delayShow={1000}>
            <span
                {...props}
                className={`tag border border-shade-opacity-200 rounded-2.5xl inline-flex items-center leading-6 ${
                    colorClasses[colorToUse]
                } ${isInteractive ? 'hover:cursor-pointer' : ''} ${props.className ?? ''}`}
            >
                <span
                    className={`${tagSizeClasses[size]} max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap`}
                >
                    {children}
                </span>

                {removable && removeClickCallback && (
                    <div
                        className="tag__remove-button opacity-30 border border-transparent inline-flex items-center justify-center rounded-full h-6 w-6 density-lg:h-8 density-lg:w-8 text-lg cursor-pointer focus:opacity-100 hover:opacity-100 active:bg-accent-opacity-100 hover:bg-accent-opacity-100 focus:border-accent-600 active:shadow-inner-md ml-0.5"
                        role="button"
                        onClick={removeClickCallback}
                        onMouseDown={removeMouseDownCallback}
                        onTouchEnd={removeTouchEndCallback}
                        tabIndex={0}
                    >
                        <CloseIcon />
                    </div>
                )}
            </span>
        </Tooltip>
    )
}
